import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { captureUtm } from "../lib/utm";
import { trackLeadSubmit, usePhoneTapTracking } from "../analytics";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Phone, ShieldCheck, HandHeart, Clock, CheckCircle } from "@phosphor-icons/react";

const PHONE = "800-719-6912";
const PHONE_TEL = "tel:8007196912";

const NAV = [
  ["Services", "/#services"],
  ["About us", "/#about"],
  ["Contact us", "/#contact"],
  ["Caregivers apply", "/apply"],
];

const SERVICES_LIST = [
  "Personal care & hygiene", "Companionship & social engagement", "Light housekeeping",
  "Meal preparation", "Medication reminders", "Transportation assistance",
  "Respite care for family caregivers", "24-hour awake care",
];

export default function GoogleAdLandingPage() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  usePhoneTapTracking();

  useEffect(() => { captureUtm(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/public/care-inquiry", { ...form, lead_kind: "google-ad-landing" });
      trackLeadSubmit();
      setSent(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-white text-[#23282f]" data-testid="gads-landing-page">
      <header className="border-b border-stone-100 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 sm:px-8">
          <div className="flex items-center gap-2.5">
            <img src="/shc-logo.png" alt="Silver Home Care" className="h-10 w-auto" />
            <p className="font-heading text-lg font-semibold tracking-tight">Silver Home Care</p>
          </div>
          <a href={PHONE_TEL} className="inline-flex items-center gap-2 rounded-full bg-[#6A3E97] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#4A235A]" data-testid="gads-header-phone">
            <Phone size={16} weight="fill" /> {PHONE}
          </a>
        </div>
        <nav className="border-t border-stone-100" data-testid="gads-nav">
          <div className="mx-auto flex max-w-6xl gap-6 overflow-x-auto px-5 py-2.5 text-sm font-medium text-stone-600 sm:px-8">
            {NAV.map(([label, href]) => (
              <a key={label} href={href} className="whitespace-nowrap transition-colors hover:text-[#6A3E97]">{label}</a>
            ))}
          </div>
        </nav>
      </header>

      <section className="bg-[#F5F2FA]">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-12 sm:px-8 lg:grid-cols-2 lg:gap-14 lg:py-16">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[#6A3E97]">Non-medical home care · Bucks &amp; Philadelphia Counties</p>
            <h1 className="mt-3 font-heading text-4xl font-medium leading-[1.1] tracking-tight text-[#23282f] sm:text-5xl" data-testid="gads-headline">
              The right help at home changes everything.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-stone-600">
              Personal care, meals, housekeeping, rides, companionship — dependable help, visit
              after visit, so the person you love can stay in their own home. And it starts with
              one call.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <a href={PHONE_TEL} className="rounded-md bg-[#6A3E97] px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#55317A]" data-testid="gads-call-btn">
                Call {PHONE}
              </a>
              <a href="#callback" className="rounded-md border-2 border-[#6A3E97] px-7 py-3.5 text-base font-semibold text-[#6A3E97] transition-colors hover:bg-[#6A3E97] hover:text-white" data-testid="gads-form-link">
                Request a call back
              </a>
            </div>
            <p className="mt-3 text-sm font-medium text-stone-600">
              You'll talk to a person in our Fairless Hills office — we will call you back the same day,
              7 days a week. That's how we do things.
            </p>
            <div className="mt-8 grid max-w-xl gap-4 sm:grid-cols-2" data-testid="gads-hooks">
              <div className="rounded-xl border border-[#6A3E97]/25 bg-white p-5 shadow-sm">
                <p className="font-heading text-3xl font-medium tracking-tight text-[#6A3E97]" data-testid="gads-price">
                  Starting at $25/hr.
                </p>
              </div>
              <div className="rounded-xl border border-[#6A3E97]/25 bg-white p-5 shadow-sm">
                <p className="text-base leading-relaxed text-[#23282f]">
                  You may be eligible for <span className="font-semibold">free in-home services</span> —{" "}
                  <a href={PHONE_TEL} className="font-semibold text-[#6A3E97] hover:underline" data-testid="gads-chc-call">give us a call</a>{", "}
                  or <a href="#callback" className="font-semibold text-[#6A3E97] hover:underline" data-testid="gads-chc-form">leave your number below</a>{" "}
                  and ask us how to apply.
                </p>
              </div>
            </div>
          </div>
          <div className="mx-auto w-full max-w-sm lg:max-w-xs">
            <video controls preload="metadata" playsInline poster="/shc-hero-welcome-cool.webp"
              className="mx-auto block aspect-[9/16] w-full rounded-2xl bg-black object-cover shadow-lg" data-testid="gads-video">
              <source src="/ad-peace-of-mind.mp4" type="video/mp4" />
              Your browser can't play this video — call us at {PHONE}.
            </video>
            <p className="mt-3 text-center text-sm text-stone-500">A quick word from us — about a minute.</p>
          </div>
        </div>
      </section>

      <section className="border-b border-stone-100 bg-white">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-5 py-8 sm:grid-cols-3 sm:px-8" data-testid="gads-trust">
          {[
            { icon: ShieldCheck, title: "Licensed & local since 2016", text: "A Pennsylvania-licensed, non-medical home care agency — locally owned and hands-on, ten years in Bucks County." },
            { icon: HandHeart, title: "Matched, not just scheduled", text: "Your caregiver is matched to the person and introduced face to face before the first visit — so day one already feels familiar." },
            { icon: Clock, title: "Management that stays involved", text: "When you call, a person picks up. And the office stays involved and responsive." },
          ].map((t) => (
            <div key={t.title} className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#6A3E97]/10">
                <t.icon size={22} weight="duotone" className="text-[#6A3E97]" />
              </span>
              <div>
                <p className="font-heading text-lg font-medium tracking-tight text-[#23282f]">{t.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-stone-600">{t.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-b border-stone-100 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8 lg:py-14" data-testid="gads-services">
          <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[#6A3E97]">What we help with</p>
          <div className="mt-5 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
            {SERVICES_LIST.map((s) => (
              <p key={s} className="border-b border-stone-100 py-3 text-base font-medium text-[#23282f]">{s}</p>
            ))}
          </div>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-stone-600">
            Non-medical, everyday help — the things that keep a household running and a person independent.
            Not on the list? Ask — if it's non-medical and it helps someone stay home, we probably do it.
          </p>
        </div>
      </section>

      <section id="callback" className="bg-[#F5F2FA]">
        <div className="mx-auto grid max-w-6xl scroll-mt-10 gap-10 px-5 py-12 sm:px-8 lg:grid-cols-2 lg:gap-14 lg:py-16" data-testid="gads-callback">
          <div>
            <h2 className="font-heading text-3xl font-medium tracking-tight text-[#23282f] sm:text-4xl">Talk to a person.</h2>
            <p className="mt-4 max-w-md text-lg leading-relaxed text-stone-600">
              Tell us about the care that's needed and what the goals are — and we can talk about
              whether we might be a good fit.
            </p>
            <a href={PHONE_TEL} className="mt-8 flex items-center gap-3" data-testid="gads-phone-block">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#6A3E97]/10"><Phone size={20} weight="fill" className="text-[#6A3E97]" /></span>
              <span><span className="block text-2xl font-semibold tracking-tight text-[#23282f]">{PHONE}</span>
              <span className="text-sm text-stone-500">Mon–Fri 9–5 for routine matters · on-shift support & scheduling 24/7</span></span>
            </a>
            <p className="mt-4 pl-14 text-xs text-stone-500">Not affiliated with Silver Home Care agencies in Michigan or Connecticut.</p>
          </div>

          <div className="h-fit rounded-xl border border-stone-200 bg-white p-6 shadow-lg sm:p-7" data-testid="gads-form-card">
            {sent ? (
              <div className="text-center" data-testid="gads-success">
                <p className="mt-3 font-heading text-2xl font-medium text-[#23282f]">We'll call you back.</p>
                <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-stone-600">
                  Thank you{form.name ? `, ${form.name.split(" ")[0]}` : ""}. Someone from our office will call you —
                  usually the same day. Rather not wait? Call us now at{" "}
                  <a href={PHONE_TEL} className="font-semibold text-[#6A3E97] hover:underline">{PHONE}</a>.
                </p>
                <CheckCircle size={40} weight="fill" className="mx-auto mt-5 text-[#6A3E97]" />
              </div>
            ) : (
              <div data-testid="gads-form">
                <p className="font-heading text-2xl font-medium tracking-tight text-[#23282f]">Request a call back</p>
                <p className="mt-1 text-sm text-stone-600">Free, no obligation. Tell us what's going on and we'll help you sort it out.</p>
                <div className="mt-5 space-y-4">
                  <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Your name *" className="h-12 border-stone-300 text-base" data-testid="gads-name" />
                  <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="Phone *" type="tel" className="h-12 border-stone-300 text-base" data-testid="gads-phone" />
                  <textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    placeholder="Anything we should know? (optional)" rows={3}
                    className="w-full rounded-md border border-stone-300 px-3 py-2.5 text-base text-[#23282f]" data-testid="gads-message" />
                  <Button onClick={submit} disabled={busy || !form.name.trim() || !form.phone.trim()} size="lg"
                    className="w-full rounded-md bg-[#6A3E97] px-8 py-3.5 text-base font-semibold text-white hover:bg-[#55317A]"
                    data-testid="gads-submit">
                    {busy ? "Sending…" : "Request a call back"}
                  </Button>
                  <p className="text-center text-xs text-stone-500">Or call us right now —{" "}
                    <a href={PHONE_TEL} className="font-semibold text-[#23282f] hover:text-[#6A3E97]">{PHONE}</a>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-6 px-5 py-10 sm:px-8" data-testid="gads-final-cta">
          <p className="font-heading text-2xl font-medium tracking-tight text-[#23282f] sm:text-3xl">
            The hardest part is making the call.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <a href={PHONE_TEL} className="rounded-md bg-[#6A3E97] px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#55317A]" data-testid="gads-final-call">
              Call {PHONE}
            </a>
            <a href="#callback" className="text-base font-semibold text-[#6A3E97] hover:underline" data-testid="gads-final-form">Or request a call back →</a>
          </div>
        </div>
      </section>

      <footer className="bg-[#2E1A3E] py-8 text-white">
        <div className="mx-auto max-w-6xl px-5 text-xs text-white/50 sm:px-8">
          <p>© {new Date().getFullYear()} Silver Home Care · Fairless Hills, PA · {PHONE} · Not affiliated with Silver Home Care agencies in CT or MI (<a href="https://silverhomecare.org" target="_blank" rel="noreferrer" className="underline hover:text-white/80">silverhomecare.org</a>)</p>
        </div>
      </footer>
    </div>
  );
}
